import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { apiError, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import JobWizardStepRail from "@/components/JobWizardStepRail";
import JobWizardAdvancePanel from "@/components/JobWizardAdvancePanel";
import JobWizardDocumentFolders from "@/components/JobWizardDocumentFolders";
import JobWizardFieldRequirementsDialog from "@/components/JobWizardFieldRequirementsDialog";
import JobWizardRoleAssignments from "@/components/JobWizardRoleAssignments";
import {
  CaretLeft,
  CaretRight,
  Check,
  FileArrowUp,
  FloppyDisk,
  GearSix,
  Plus,
  Trash,
  X,
} from "@phosphor-icons/react";

const DRAFT_KEY = "dvepl-job-allotment-draft-v1";

const STEPS = [
  {
    key: "customer",
    label: "Customer",
    title: "Customer Details",
    required: true,
  },
  {
    key: "project",
    label: "Project Details",
    title: "Project Details",
    required: true,
  },
  {
    key: "schedule",
    label: "Schedule & Advance",
    title: "Schedule, Rates & Advance",
  },
  {
    key: "roles",
    label: "Core Roles & Drawing",
    title: "Core Incharge Roles & Drawing Target",
    required: true,
  },
  { key: "documents", label: "Documents", title: "Documents & Finalize" },
];

const ROLE_DEFS = [
  { key: "po_incharge", label: "PO Incharge", short: "PO", tone: "teal" },
  {
    key: "drawings_incharge",
    label: "DRG Incharge",
    short: "DRG",
    tone: "green",
  },
  {
    key: "design_incharge",
    label: "Design Incharge",
    short: "DESIGN",
    tone: "amber",
  },
  {
    key: "production_incharge",
    label: "Prod Incharge",
    short: "PROD",
    tone: "emerald",
  },
];

const INPUT_CLASS =
  "h-9 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 " +
  "focus:border-emerald-600 focus-visible:ring-emerald-600";

const LABEL_CLASS =
  "mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-slate-600";

const WIZARD_FIELDS = [
  { key: "customer", label: "Customer / Company", step: 0 },
  { key: "contact_person", label: "Contact Person", step: 0 },
  { key: "customer_whatsapp", label: "Mobile / Phone", step: 0 },
  { key: "customer_email", label: "Email Address", step: 0 },
  { key: "customer_gst", label: "GST / Tax ID", step: 0 },
  { key: "billing_address", label: "Billing Address", step: 0 },
  { key: "customer_code", label: "DVEPL Job Code", step: 1 },
  { key: "order_date", label: "Order Confirm Date", step: 1 },
  { key: "taken_by", label: "Order Taken By", step: 1 },
  { key: "project_reference", label: "Project Reference / CA No", step: 1 },
  { key: "target_dispatch", label: "Delivery Target Commitment", step: 1 },
  { key: "priority", label: "Priority Level", step: 1 },
  { key: "items", label: "Schedule Line Items", step: 2 },
  { key: "item_description", label: "Line Description", step: 2 },
  { key: "item_qty", label: "Line Quantity", step: 2 },
  { key: "item_unit", label: "Line Unit", step: 2 },
  { key: "item_rate", label: "Line Unit Rate", step: 2 },
  { key: "base_value", label: "Fallback Basic Value", step: 2 },
  { key: "advance_entries", label: "Advance Entries", step: 2 },
  { key: "role_assignments", label: "Incharge Role Assignments", step: 3 },
  { key: "drawing_target", label: "Drawing Target Date", step: 3 },
  { key: "document_folders", label: "Document Folders", step: 4 },
  { key: "documents", label: "Documents", step: 4 },
];

function blankForm() {
  return {
    customer: "",
    contact_person: "",
    customer_email: "",
    customer_whatsapp: "",
    customer_gst: "",
    billing_address: "",
    shipping_address: "",
    customer_code: "",
    project_reference: "",
    order_date: new Date().toISOString().slice(0, 10),
    target_dispatch: "",
    commitment_mode: "fixed",
    commitment_days: "45",
    priority: "normal",
    taken_by: "",
    total_units: "",
    base_value: "",
    role_assignments: {},
    role_definitions: ROLE_DEFS,
    advance_entries: [],
    document_folders: [{ id: "general", name: "General" }],
    drawing_target: "",
    items: [{ description: "", qty: 1, unit: "Nos", rate: "" }],
  };
}

function numberOf(value) {
  return Number(value) || 0;
}

function userName(users, id) {
  return users.find((user) => user.id === id)?.name || "Unassigned";
}

export default function JobAllotmentWizard({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(blankForm);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [scheduleTab, setScheduleTab] = useState("items");
  const [requiredFields, setRequiredFields] = useState([]);
  const [persistedRequiredFields, setPersistedRequiredFields] = useState([]);
  const [fieldRulesOpen, setFieldRulesOpen] = useState(false);
  const [savingFieldRules, setSavingFieldRules] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/users?active=true").then((response) => setUsers(response.data));
    api
      .get("/workflow-templates")
      .then((response) => setTemplates(response.data));
    api
      .get("/customers")
      .then((response) => setCustomers(response.data.slice(0, 100)));
    api.get("/document_types").then((response) => setDocTypes(response.data));
    api
      .get("/settings/job-wizard-form")
      .then((response) => {
        const required = response.data.required || [];
        setRequiredFields(required);
        setPersistedRequiredFields(required);
      });
    const saved = localStorage.getItem(DRAFT_KEY);
    if (saved) {
      try {
        setForm({ ...blankForm(), ...JSON.parse(saved) });
      } catch {
        localStorage.removeItem(DRAFT_KEY);
      }
    }
  }, [open]);

  const set = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));
  const selectedTemplate = templates[0];
  const lineTotals = useMemo(
    () => form.items.map((item) => numberOf(item.qty) * numberOf(item.rate)),
    [form.items],
  );
  const baseValue =
    lineTotals.reduce((sum, value) => sum + value, 0) ||
    numberOf(form.base_value);
  const gst = Math.round(baseValue * 0.18);
  const totalValue = baseValue + gst;
  const selectedRoleIds = [...new Set(Object.values(form.role_assignments).flat())];
  const totalSerials = form.items.filter((item) => item.description.trim()).length;
  const canManageFieldRules = ["admin", "manager"].includes(user?.role);

  const isRequired = (field) => requiredFields.includes(field);
  const requiredLabel = (label, field) => `${label}${isRequired(field) ? " *" : ""}`;

  const chooseCustomer = (customerId) => {
    const customer = customers.find((item) => item.id === customerId);
    if (!customer) return;
    setForm((current) => ({
      ...current,
      customer: customer.name || current.customer,
      contact_person: customer.contact_person || current.contact_person,
      customer_email: customer.email || current.customer_email,
      customer_whatsapp: customer.mobile || current.customer_whatsapp,
      customer_gst: customer.gst || current.customer_gst,
      billing_address: customer.billing_address || current.billing_address,
    }));
  };

  const updateItem = (index, key, value) => {
    set(
      "items",
      form.items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item,
      ),
    );
  };

  const addItem = () => {
    set("items", [
      ...form.items,
      { description: "", qty: 1, unit: "Nos", rate: "" },
    ]);
  };

  const removeItem = (index) => {
    if (form.items.length === 1) return;
    set(
      "items",
      form.items.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  const saveDraft = () => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    toast.success("Job allotment draft saved on this device");
  };

  const saveFieldRules = async () => {
    setSavingFieldRules(true);
    try {
      const { data } = await api.put("/settings/job-wizard-form", {
        required: requiredFields,
      });
      const required = data.required || [];
      setRequiredFields(required);
      setPersistedRequiredFields(required);
      setFieldRulesOpen(false);
      toast.success("Job wizard field rules saved for future jobs");
    } catch (error) {
      toast.error(apiError(error.response?.data?.detail) || "Could not save field rules");
    } finally {
      setSavingFieldRules(false);
    }
  };

  const toggleFieldRule = (field) => {
    setRequiredFields((current) =>
      current.includes(field)
        ? current.filter((key) => key !== field)
        : [...current, field],
    );
  };

  const setFieldRulesOpenState = (value) => {
    if (!value) setRequiredFields(persistedRequiredFields);
    setFieldRulesOpen(value);
  };

  const validateStep = () => {
    const values = {
      customer: form.customer.trim(),
      contact_person: form.contact_person.trim(),
      customer_whatsapp: form.customer_whatsapp.trim(),
      customer_email: form.customer_email.trim(),
      customer_gst: form.customer_gst.trim(),
      billing_address: form.billing_address.trim(),
      customer_code: form.customer_code.trim(),
      order_date: form.order_date,
      taken_by: form.taken_by,
      project_reference: form.project_reference.trim(),
      target_dispatch:
        form.commitment_mode === "relative"
          ? Number(form.commitment_days) > 0
          : form.target_dispatch,
      items: form.items.some((item) => item.description.trim()),
      item_description: form.items.some((item) => item.description.trim()),
      item_qty: form.items.some((item) => Number(item.qty) > 0),
      item_unit: form.items.some((item) => item.unit.trim()),
      item_rate: form.items.some((item) => Number(item.rate) > 0),
      priority: form.priority,
      base_value: Number(form.base_value) > 0,
      advance_entries: form.advance_entries.some((entry) => Number(entry.amount) > 0),
      role_assignments: selectedRoleIds.length > 0,
      drawing_target: form.drawing_target,
      document_folders: form.document_folders.some((folder) => folder.id !== "general"),
      documents: files.length > 0,
    };
    const missing = WIZARD_FIELDS.filter(
      (field) => field.step === step && isRequired(field.key) && !values[field.key],
    );
    if (missing.length) {
      toast.error(`Complete compulsory fields: ${missing.map((field) => field.label).join(", ")}`);
      return false;
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((current) => Math.min(current + 1, STEPS.length - 1));
  };

  const readFile = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () =>
        resolve({
          name: file.name,
          type: file.type,
          size: file.size,
          data: reader.result,
        });
      reader.readAsDataURL(file);
    });

  const createJob = async () => {
    if (!validateStep()) return;
    if (!selectedTemplate?.id) {
      toast.error("No workflow template is configured yet");
      return;
    }
    setSaving(true);
    try {
      const details = [
        { title: "Project Reference / CA No", value: form.project_reference },
        {
          title: "Basic Value",
          value: baseValue ? `₹ ${baseValue.toLocaleString("en-IN")}` : "",
        },
        {
          title: "GST 18%",
          value: gst ? `₹ ${gst.toLocaleString("en-IN")}` : "",
        },
        {
          title: "Total Order Value",
          value: totalValue ? `₹ ${totalValue.toLocaleString("en-IN")}` : "",
        },
        {
          title: "Total S.No",
          value: totalSerials ? String(totalSerials) : "",
        },
        { title: "Drawing Target", value: form.drawing_target },
      ].filter((detail) => detail.value);
      const payload = {
        name: form.customer || form.project_reference || form.customer_code || "New Job",
        customer: form.customer,
        contact_person: form.contact_person,
        customer_code: form.customer_code,
        customer_email: form.customer_email,
        customer_whatsapp: form.customer_whatsapp,
        customer_gst: form.customer_gst,
        billing_address: form.billing_address,
        shipping_address: form.shipping_address || form.billing_address,
        order_date: form.order_date,
        target_dispatch:
          form.commitment_mode === "fixed" ? form.target_dispatch : null,
        commitment_mode: form.commitment_mode,
        commitment_days:
          form.commitment_mode === "relative"
            ? numberOf(form.commitment_days)
            : null,
        taken_by: form.taken_by || null,
        job_responsibility: selectedRoleIds,
        role_assignments: form.role_assignments,
        role_definitions: form.role_definitions,
        template_id: selectedTemplate.id,
        priority: form.priority,
        total_units: totalSerials || null,
        details,
        po_items: form.items.filter((item) => item.description.trim()),
        advance_entries: form.advance_entries.filter((entry) => Number(entry.amount) > 0),
        document_folders: form.document_folders,
        reminders_enabled: true,
        reminder_value: 2,
        reminder_unit: "days",
        reminder_channels: ["email", "whatsapp", "in_app"],
        assignees: {},
      };
      const { data: order } = await api.post("/orders", payload);
      if (payload.po_items.length > 0) {
        await api.put(`/orders/${order.id}`, { po_items: payload.po_items });
      }
      for (const item of files) {
        if (item.file.size > 50 * 1024 * 1024) continue;
        const attachment = await readFile(item.file);
        await api.post(`/orders/${order.id}/attachments`, {
          ...attachment,
          category: item.category,
          folder_id: item.folder_id,
          folder_name: item.folder_name,
        });
      }
      localStorage.removeItem(DRAFT_KEY);
      toast.success(`Job ${order.order_no} created and allotted`);
      setForm(blankForm());
      setFiles([]);
      setStep(0);
      onOpenChange(false);
      onCreated?.(order);
    } catch (error) {
      toast.error(apiError(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    onOpenChange(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[94vh] w-[min(98vw,1050px)] max-w-none flex-col gap-0 overflow-hidden border-slate-200 bg-slate-50 p-0 text-slate-900 !opacity-100"
        data-testid="job-allotment-wizard"
      >
        <div className="flex items-center justify-between bg-gradient-to-r from-emerald-950 via-emerald-900 to-slate-900 px-5 py-4 text-white">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-emerald-400 text-emerald-950">
              <FileArrowUp size={19} weight="bold" />
            </div>
            <div className="min-w-0">
              <DialogTitle
                className="font-heading text-lg font-extrabold"
                data-testid="job-wizard-title"
              >
                Create New Job Allotment
              </DialogTitle>
              <div className="text-xs text-emerald-100/80">
                {STEPS[step].title} · Step {step + 1} of {STEPS.length}
              </div>
              <DialogDescription className="sr-only">
                Create a job with customer, project, item, role, and document information.
              </DialogDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canManageFieldRules && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setFieldRulesOpenState(true)}
                data-testid="job-wizard-field-rules-open"
                className="hidden h-8 border-emerald-300 bg-white/10 text-xs text-emerald-50 hover:bg-white/20 hover:text-white sm:flex"
              >
                <GearSix size={15} /> Field rules
              </Button>
            )}
            <Button
              type="button"
              onClick={saveDraft}
              data-testid="job-wizard-save-draft"
              className="hidden h-8 bg-emerald-500/20 text-xs text-emerald-50 hover:bg-emerald-500/35 sm:flex"
            >
              <FloppyDisk size={14} /> Save Draft
            </Button>
            <button
              type="button"
              onClick={close}
              className="p-1.5 text-emerald-100/70 transition-colors hover:text-white"
              data-testid="job-wizard-close"
              aria-label="Close job allotment wizard"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <JobWizardStepRail
          steps={STEPS}
          currentStep={step}
          onSelect={setStep}
        />

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {step === 0 && (
            <section
              className="space-y-4"
              data-testid="job-wizard-customer-step"
            >
              <div className="border border-emerald-200 bg-emerald-50 p-4">
                <h2 className="font-heading text-base font-bold text-emerald-950">
                  Step 1: Customer Details
                </h2>
                <p className="mt-1 text-xs leading-relaxed text-emerald-800">
                  Select an existing customer or capture a fresh customer and
                  contact record for this job.
                </p>
              </div>
              <div className="border border-slate-200 bg-white p-4">
                <label className={LABEL_CLASS}>
                  Quick auto-fill from existing customers
                </label>
                <Select onValueChange={chooseCustomer}>
                  <SelectTrigger
                    className={INPUT_CLASS}
                    data-testid="job-wizard-customer-lookup"
                  >
                    <SelectValue placeholder="Search a customer record to auto-fill contact details" />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-slate-900">
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                        {customer.contact_person
                          ? ` · ${customer.contact_person}`
                          : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 gap-3 border border-slate-200 bg-white p-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className={LABEL_CLASS}>{requiredLabel("Customer / Company Name", "customer")}</label>
                  <Input
                    value={form.customer}
                    onChange={(event) => set("customer", event.target.value)}
                    className={INPUT_CLASS}
                    data-testid="job-customer-name"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>{requiredLabel("Contact Person", "contact_person")}</label>
                  <Input
                    value={form.contact_person}
                    onChange={(event) =>
                      set("contact_person", event.target.value)
                    }
                    className={INPUT_CLASS}
                    data-testid="job-contact-person"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>{requiredLabel("Mobile / Phone No.", "customer_whatsapp")}</label>
                  <Input
                    value={form.customer_whatsapp}
                    onChange={(event) =>
                      set("customer_whatsapp", event.target.value)
                    }
                    className={INPUT_CLASS}
                    data-testid="job-customer-phone"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>{requiredLabel("Email Address", "customer_email")}</label>
                  <Input
                    type="email"
                    value={form.customer_email}
                    onChange={(event) =>
                      set("customer_email", event.target.value)
                    }
                    className={INPUT_CLASS}
                    data-testid="job-customer-email"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>{requiredLabel("GST / Tax ID", "customer_gst")}</label>
                  <Input
                    value={form.customer_gst}
                    onChange={(event) =>
                      set("customer_gst", event.target.value)
                    }
                    className={INPUT_CLASS}
                    data-testid="job-customer-gst"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>{requiredLabel("Billing Address", "billing_address")}</label>
                  <Input
                    value={form.billing_address}
                    onChange={(event) =>
                      set("billing_address", event.target.value)
                    }
                    className={INPUT_CLASS}
                    data-testid="job-billing-address"
                  />
                </div>
              </div>
            </section>
          )}

          {step === 1 && (
            <section
              className="space-y-4"
              data-testid="job-wizard-project-step"
            >
              <div className="border border-emerald-200 bg-emerald-50 p-4">
                <h2 className="font-heading text-base font-bold text-emerald-950">
                  Step 2: Project Details
                </h2>
                <p className="mt-1 text-xs text-emerald-800">
                  Define the job code, project reference, commitment, and workflow priority.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 border border-slate-200 bg-white p-4 md:grid-cols-3">
                <div>
                  <label className={LABEL_CLASS}>{requiredLabel("DVEPL Job Code", "customer_code")}</label>
                  <Input
                    value={form.customer_code}
                    onChange={(event) =>
                      set("customer_code", event.target.value)
                    }
                    className={INPUT_CLASS}
                    data-testid="job-code"
                    placeholder="e.g. DVEPL-9101"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>{requiredLabel("Order Confirm Date", "order_date")}</label>
                  <Input
                    type="date"
                    value={form.order_date}
                    onChange={(event) => set("order_date", event.target.value)}
                    className={INPUT_CLASS}
                    data-testid="job-order-date"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>{requiredLabel("Order Taken By", "taken_by")}</label>
                  <Select
                    value={form.taken_by}
                    onValueChange={(value) => set("taken_by", value)}
                  >
                    <SelectTrigger
                      className={INPUT_CLASS}
                      data-testid="job-taken-by"
                    >
                      <SelectValue placeholder="Select member" />
                    </SelectTrigger>
                    <SelectContent className="bg-white text-slate-900">
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-3">
                  <label className={LABEL_CLASS}>{requiredLabel("Project Reference / CA No", "project_reference")}</label>
                  <Input
                    value={form.project_reference}
                    onChange={(event) =>
                      set("project_reference", event.target.value)
                    }
                    className={INPUT_CLASS}
                    data-testid="job-project-reference"
                    placeholder="Panel / project scope reference"
                  />
                </div>
                <div className="md:col-span-3">
                  <label className={LABEL_CLASS}>{requiredLabel("Delivery Target Commitment", "target_dispatch")}</label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => set("commitment_mode", "relative")}
                      data-testid="job-commitment-relative"
                      className={`border px-3 py-2 text-xs font-semibold ${form.commitment_mode === "relative" ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 text-slate-600"}`}
                    >
                      Days after approval
                    </button>
                    <button
                      type="button"
                      onClick={() => set("commitment_mode", "fixed")}
                      data-testid="job-commitment-fixed"
                      className={`border px-3 py-2 text-xs font-semibold ${form.commitment_mode === "fixed" ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 text-slate-600"}`}
                    >
                      Fixed dispatch date
                    </button>
                    {form.commitment_mode === "relative" ? (
                      <Input
                        type="number"
                        min="1"
                        value={form.commitment_days}
                        onChange={(event) =>
                          set("commitment_days", event.target.value)
                        }
                        className="h-9 w-32 border-slate-200"
                        data-testid="job-commitment-days"
                      />
                    ) : (
                      <Input
                        type="date"
                        value={form.target_dispatch}
                        onChange={(event) =>
                          set("target_dispatch", event.target.value)
                        }
                        className="h-9 w-44 border-slate-200"
                        data-testid="job-target-dispatch"
                      />
                    )}
                  </div>
                </div>
                <div>
                  <label className={LABEL_CLASS}>Priority Level</label>
                  <Select
                    value={form.priority}
                    onValueChange={(value) => set("priority", value)}
                  >
                    <SelectTrigger
                      className={INPUT_CLASS}
                      data-testid="job-priority"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white text-slate-900">
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>
          )}

          {step === 2 && (
            <section
              className="space-y-4"
              data-testid="job-wizard-schedule-step"
            >
              <div className="border border-teal-200 bg-teal-50 p-4">
                <h2 className="font-heading text-base font-bold text-teal-950">
                  Step 3: Schedule, Rates & Advance
                </h2>
                <p className="mt-1 text-xs text-teal-800">
                  Add panel or equipment lines, then capture any advance received against the job.
                </p>
              </div>
              <div className="flex gap-1 border-b border-slate-200">
                {[{ key: "items", label: "Schedule & Rates" }, { key: "advance", label: "Advance" }].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setScheduleTab(tab.key)}
                    className={`border-b-2 px-3 py-2 text-xs font-bold ${
                      scheduleTab === tab.key
                        ? "border-emerald-600 text-emerald-700"
                        : "border-transparent text-slate-500"
                    }`}
                    data-testid={`job-schedule-tab-${tab.key}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {scheduleTab === "items" && <>
              <div className="overflow-x-auto border border-slate-200 bg-white">
                <table className="w-full min-w-[720px] text-sm">
                  <thead className="bg-slate-50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="w-10 px-3 py-3">#</th>
                      <th className="px-3 py-3">
                        Equipment / Panel Description
                      </th>
                      <th className="w-20 px-3 py-3">Qty</th>
                      <th className="w-24 px-3 py-3">Unit</th>
                      <th className="w-32 px-3 py-3">Unit Rate</th>
                      <th className="w-32 px-3 py-3 text-right">Total</th>
                      <th className="w-10 px-2 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {form.items.map((item, index) => (
                      <tr key={index} data-testid={`job-item-row-${index}`}>
                        <td className="px-3 py-2 font-mono text-xs text-slate-500">
                          {index + 1}
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            value={item.description}
                            onChange={(event) =>
                              updateItem(
                                index,
                                "description",
                                event.target.value,
                              )
                            }
                            className={INPUT_CLASS}
                            data-testid={`job-item-description-${index}`}
                            placeholder="Main LT panel / component"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            min="1"
                            value={item.qty}
                            onChange={(event) =>
                              updateItem(index, "qty", event.target.value)
                            }
                            className={INPUT_CLASS}
                            data-testid={`job-item-qty-${index}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            value={item.unit}
                            onChange={(event) =>
                              updateItem(index, "unit", event.target.value)
                            }
                            className={INPUT_CLASS}
                            data-testid={`job-item-unit-${index}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            min="0"
                            value={item.rate}
                            onChange={(event) =>
                              updateItem(index, "rate", event.target.value)
                            }
                            className={INPUT_CLASS}
                            data-testid={`job-item-rate-${index}`}
                          />
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs font-bold text-emerald-800">
                          ₹ {lineTotals[index].toLocaleString("en-IN")}
                        </td>
                        <td className="px-2 py-2">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            data-testid={`job-remove-item-${index}`}
                            className="p-1.5 text-slate-400 hover:text-rose-600"
                            aria-label="Remove schedule line"
                          >
                            <Trash size={15} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 border border-slate-200 bg-white p-3">
                <Button
                  type="button"
                  onClick={addItem}
                  data-testid="job-add-item"
                  className="h-8 bg-emerald-600 text-xs text-white hover:bg-emerald-700"
                >
                  <Plus size={14} /> Add another item
                </Button>
                <div className="grid grid-cols-3 gap-2 text-right text-xs">
                  <span className="border border-slate-200 px-3 py-2">
                    Basic: <b>₹ {baseValue.toLocaleString("en-IN")}</b>
                  </span>
                  <span className="border border-slate-200 px-3 py-2">
                    GST 18%: <b>₹ {gst.toLocaleString("en-IN")}</b>
                  </span>
                  <span className="border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-800" data-testid="job-total-serials">
                    Total S.No: <b>{totalSerials}</b>
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 border border-slate-200 bg-white p-4 md:grid-cols-2">
                <div>
                  <label className={LABEL_CLASS}>Total S.No</label>
                  <div
                    className="h-9 border border-emerald-200 bg-emerald-50 px-3 py-2 font-mono text-sm font-bold text-emerald-800"
                    data-testid="job-total-serials-field"
                  >
                    {totalSerials}
                  </div>
                </div>
                <div>
                  <label className={LABEL_CLASS}>
                    Fallback Basic Value (₹)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={form.base_value}
                    onChange={(event) => set("base_value", event.target.value)}
                    className={INPUT_CLASS}
                    data-testid="job-base-value"
                  />
                </div>
              </div>
              </>}
              {scheduleTab === "advance" && (
                <JobWizardAdvancePanel
                  entries={form.advance_entries}
                  onChange={(advance_entries) => set("advance_entries", advance_entries)}
                />
              )}
            </section>
          )}

          {step === 3 && (
            <section className="space-y-4" data-testid="job-wizard-roles-step">
              <JobWizardRoleAssignments
                roles={form.role_definitions}
                users={users}
                assignments={form.role_assignments}
                onAssignmentsChange={(role_assignments) => set("role_assignments", role_assignments)}
                onRolesChange={(role_definitions) => set("role_definitions", role_definitions)}
              />
              <div className="grid grid-cols-1 gap-3 border border-slate-200 bg-white p-4 md:grid-cols-2">
                <div>
                  <label className={LABEL_CLASS}>{requiredLabel("Drawing Target Date", "drawing_target")}</label>
                  <Input
                    type="date"
                    value={form.drawing_target}
                    onChange={(event) =>
                      set("drawing_target", event.target.value)
                    }
                    className={INPUT_CLASS}
                    data-testid="job-drawing-target"
                  />
                </div>
                <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                    Assigned workflow team
                  </div>
                  <div className="mt-1 text-sm font-semibold text-emerald-950">
                    {selectedRoleIds.length
                      ? selectedRoleIds
                          .map((id) => userName(users, id))
                          .join(" · ")
                      : "Assign roles above"}
                  </div>
                </div>
              </div>
            </section>
          )}

          {step === 4 && (
            <section
              className="space-y-4"
              data-testid="job-wizard-documents-step"
            >
              <div className="border border-emerald-200 bg-emerald-50 p-4">
                <h2 className="font-heading text-base font-bold text-emerald-950">
                  Step 5: Documents & Finalize
                </h2>
                <p className="mt-1 text-xs text-emerald-800">
                  Attach customer PO, drawings, specifications, or any job
                  starter documents.
                </p>
              </div>
              <JobWizardDocumentFolders
                folders={form.document_folders}
                onFoldersChange={(document_folders) => set("document_folders", document_folders)}
                files={files}
                onFilesChange={setFiles}
              />
              {!!docTypes.length && (
                <div className="flex flex-wrap gap-2 border border-slate-200 bg-white p-3" data-testid="job-document-type-suggestions">
                  {docTypes.map((type) => (
                    <span key={type.id} className="border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-600">
                      {type.name}
                    </span>
                  ))}
                </div>
              )}
              <div className="border border-slate-200 bg-white p-4">
                <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-600">
                  Ready to create
                </div>
                <div className="grid gap-2 text-sm md:grid-cols-2">
                  <span>
                    <b>Customer:</b> {form.customer || "—"}
                  </span>
                  <span>
                    <b>Project:</b> {form.project_reference || "—"}
                  </span>
                  <span>
                    <b>Core roles:</b> {selectedRoleIds.length}
                  </span>
                  <span>
                    <b>Documents:</b> {files.length}
                  </span>
                  <span>
                    <b>Folders:</b> {form.document_folders.length}
                  </span>
                </div>
                {files.map((item, index) => (
                  <div
                    key={`${item.file.name}-${index}`}
                    className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2 text-xs text-slate-600"
                  >
                    <span>{item.file.name} · {item.folder_name || "General"}</span>
                    <button
                      type="button"
                      onClick={() =>
                        setFiles((current) =>
                          current.filter((_, fileIndex) => fileIndex !== index),
                        )
                      }
                      data-testid={`job-remove-document-${index}`}
                      className="text-rose-600"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 bg-white px-5 py-3">
          <Button
            type="button"
            variant="outline"
            disabled={step === 0}
            onClick={() => setStep((current) => current - 1)}
            data-testid="job-wizard-previous"
            className="border-slate-200 bg-white text-slate-700"
          >
            <CaretLeft size={15} /> Previous
          </Button>
          <div className="flex gap-2">
            {step < STEPS.length - 1 ? (
              <Button
                type="button"
                onClick={next}
                data-testid={`job-creation-step-${step + 1}-next`}
                className="bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Next: {STEPS[step + 1].label} <CaretRight size={15} />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={createJob}
                disabled={saving}
                data-testid="job-wizard-create"
                className="bg-emerald-600 text-white hover:bg-emerald-700"
              >
                {saving ? "Creating…" : "Create & Allot Job"}{" "}
                <Check size={15} weight="bold" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
      <JobWizardFieldRequirementsDialog
        open={fieldRulesOpen}
        onOpenChange={setFieldRulesOpenState}
        fields={WIZARD_FIELDS}
        requiredFields={requiredFields}
        onToggle={toggleFieldRule}
        onSave={saveFieldRules}
        saving={savingFieldRules}
      />
    </>
  );
}
